import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ChevronRight, ArrowRight } from "lucide-react";
import { getGuide, getRelatedGuides, JOURNEYS } from "@/data/guides";
import Seo, { SITE_URL, COLLECTION_NAMES } from "@/components/Seo";

export default function Guide() {
  const { slug } = useParams();
  const g = getGuide(slug);
  if (!g) return <Navigate to="/guides" replace />;

  const canonical = `${SITE_URL}/guides/${g.slug}`;
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: g.title,
    description: g.metaDescription,
    author: { "@type": "Organization", name: "Hydronic Heating Controls and Valves" },
    publisher: { "@type": "Organization", name: "Hydronic Heating Controls and Valves" },
    mainEntityOfPage: canonical,
    datePublished: "2026-01-15",
    dateModified: "2026-06-01",
  };
  const breadcrumbLd = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE_URL}/guides` },
      { "@type": "ListItem", position: 3, name: g.title, item: canonical },
    ],
  };

  const more = getRelatedGuides(g.slug, 3);

  return (
    <article className="max-w-3xl mx-auto px-4 py-12" data-testid={`guide-${g.slug}`}>
      <Seo title={g.title} description={g.metaDescription} jsonLd={[articleLd, breadcrumbLd]} />

      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-primary">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/guides" className="hover:text-primary">Guides</Link>
      </nav>

      <h1 className="mt-4 text-3xl sm:text-4xl font-display font-black tracking-tight leading-tight">{g.title}</h1>
      <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <Clock className="w-3.5 h-3.5" /> {g.readTime}
      </div>

      <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{g.intro}</p>

      {g.journey && JOURNEYS[g.journey] && (
        <nav className="mt-8 rounded-sm border border-border bg-muted/40 p-5" data-testid="guide-journey" aria-label={JOURNEYS[g.journey].heading}>
          <span className="text-overline text-primary">{JOURNEYS[g.journey].heading}</span>
          <ol className="mt-3 flex flex-wrap items-center gap-x-1.5 gap-y-2">
            {JOURNEYS[g.journey].steps.map((step, i) => {
              const isCurrent = step.to === `/guides/${g.slug}`;
              return (
                <li key={step.to} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />}
                  {isCurrent ? (
                    <span className="text-sm font-bold text-foreground" data-testid="journey-current" aria-current="step">
                      <span className="inline-grid place-items-center w-5 h-5 rounded-full bg-primary text-white text-[11px] font-bold mr-1.5 align-middle">{i + 1}</span>
                      {step.label}
                    </span>
                  ) : (
                    <Link to={step.to} data-testid={`journey-step-${i + 1}`} className="text-sm font-medium text-primary hover:underline underline-offset-2">
                      <span className="inline-grid place-items-center w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold mr-1.5 align-middle">{i + 1}</span>
                      {step.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <div className="mt-8 space-y-8">
        {g.sections.map((s) => (
          <section key={s.h}>
            <h2 className="text-xl font-display font-bold tracking-tight">{s.h}</h2>
            {s.p && <p className="mt-2 text-muted-foreground leading-relaxed">{s.p}</p>}
            {s.table && (
              <div className="mt-4 overflow-x-auto rounded-sm border border-border" data-testid="guide-table">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-muted">
                      {s.table.headers.map((th) => (
                        <th key={th} className="text-left font-semibold text-foreground px-4 py-2.5 border-b border-border">{th}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.table.rows.map((row, ri) => (
                      <tr key={ri} className="odd:bg-card even:bg-muted/40">
                        {row.map((cell, ci) => (
                          <td key={ci} className="px-4 py-2.5 align-top text-muted-foreground border-b border-border last:border-b-0">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {s.cta && (
              <Link
                to={s.cta.to}
                data-testid="guide-section-cta"
                className="mt-4 inline-flex items-center gap-2 bg-accent text-white px-5 py-3 rounded-sm font-semibold hover:bg-accent/90 transition-colors">
                {s.cta.label} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            {s.link && (
              <a
                href={s.link.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="guide-section-link"
                className="mt-4 inline-flex items-center gap-2 border border-primary text-primary px-5 py-3 rounded-sm font-semibold hover:bg-primary hover:text-white transition-colors">
                {s.link.label} <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </section>
        ))}
      </div>

      {g.ctaPaths?.length > 0 && (
        <div className="mt-10 grid gap-3 sm:grid-cols-2" data-testid="guide-cta-paths">
          {g.ctaPaths.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={`guide-cta-path-${c.primary ? "shop" : "photo"}`}
              className={`flex items-center justify-between gap-3 px-5 py-4 rounded-sm font-semibold transition-colors ${c.primary ? "bg-accent text-white hover:bg-accent/90" : "border border-primary text-primary hover:bg-primary hover:text-white"}`}>
              {c.label} <ArrowRight className="w-4 h-4 shrink-0" />
            </Link>
          ))}
        </div>
      )}

      {g.related?.length > 0 && (
        <div className="mt-12 border-t border-border pt-8">
          <span className="text-overline text-primary">Shop related</span>
          <div className="mt-3 flex flex-wrap gap-3">
            {g.related.map((c) => (
              <Link
                key={c}
                to={`/collections/${c}`}
                data-testid={`guide-related-${c}`}
                className="inline-flex items-center gap-2 border border-border bg-card px-4 py-2.5 rounded-sm text-sm font-semibold hover:border-primary transition-colors">
                {COLLECTION_NAMES[c] || c} <ArrowRight className="w-4 h-4 text-primary" />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12 steel-texture text-white rounded-sm p-8">
        <h2 className="text-xl font-display font-bold">Need help finding the right part?</h2>
        <p className="mt-2 text-sm text-white/80 leading-relaxed">Our team has over 80 years' combined heating experience. Tell us what you need and we'll help you get it right.</p>
        <Link to="/contact" className="mt-4 inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-sm font-semibold hover:bg-white/90 transition-colors">Contact us <ArrowRight className="w-4 h-4" /></Link>
      </div>

      {more.length > 0 && (
        <div className="mt-14" data-testid="related-guides">
          <h2 className="text-lg font-display font-bold">Related guides you might like</h2>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            {more.map((m) => (
              <Link key={m.slug} to={`/guides/${m.slug}`} data-testid={`related-guide-${m.slug}`} className="group border border-border rounded-sm bg-card p-4 hover:border-primary transition-colors">
                <h3 className="text-sm font-semibold leading-snug group-hover:text-primary transition-colors">{m.title}</h3>
                {m.readTime && <span className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {m.readTime}</span>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
